import {
  AudioOutlined,
  DotChartOutlined,
  FileOutlined,
  FireOutlined,
  FontSizeOutlined,
  GiftOutlined,
  MessageOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, api, type OffsetPageInfo } from '../api/client';
import type { components } from '../api/schema.gen';
import type { Dictionary } from '../lib/i18n/types';
import { EmptyState } from '../components/EmptyState';
import { Modal } from '../components/Modal';
import { ModelQuickStartView } from '../components/ModelQuickStartView';
import { OnboardingNotice } from '../components/OnboardingNotice';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { OffsetPager } from '../components/Pagination';
import { SelectMenu } from '../components/SelectMenu';
import { formatDateTime } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { ModelVendorMark } from '../lib/modelVendor';
import { formatBalance } from '../lib/money';
import { fadeUp, staggerContainer } from '../lib/motionPresets';

type Model = components['schemas']['Model'];
type Mode = components['schemas']['Model']['mode'];

// LLMR-136: 'image'/'video' are reserved for the not-yet-built generation
// epic — no model carries either today — but the label lookup is generic so
// nothing here needs to change the day one ships.
function modeLabel(dict: Dictionary, mode: Mode): string {
  switch (mode) {
    case 'embedding':
      return dict.models.modeEmbedding;
    case 'image':
      return dict.models.modeImage;
    case 'video':
      return dict.models.modeVideo;
    default:
      return dict.models.modeChat;
  }
}

// The pictogram shown in the small square badge next to a model's name —
// same shape in both locales (a customer scanning the grid pattern-matches
// on the icon, not a word), colour-coded per mode below. @ant-design/icons
// is already the icon set this app uses (see the sidebar nav in Layout.tsx),
// so this reuses it rather than hand-rolling new SVGs.
function modeIcon(mode: Mode): ReactNode {
  switch (mode) {
    case 'embedding':
      return <DotChartOutlined aria-hidden="true" />;
    case 'image':
      return <PictureOutlined aria-hidden="true" />;
    case 'video':
      return <VideoCameraOutlined aria-hidden="true" />;
    default:
      return <MessageOutlined aria-hidden="true" />;
  }
}

type InputModality = Model['input_modalities'][number];
type OutputModality = Model['output_modality'];

// LLMR-136 (000044): one icon per modality — image/video/embedding reuse the
// exact glyph modeIcon() already uses for those, so the same shape means the
// same thing whether it's labelling a model's call type or its I/O.
function modalityIcon(modality: InputModality | OutputModality): ReactNode {
  switch (modality) {
    case 'image':
      return <PictureOutlined aria-hidden="true" />;
    case 'video':
      return <VideoCameraOutlined aria-hidden="true" />;
    case 'audio':
      return <AudioOutlined aria-hidden="true" />;
    case 'file':
      return <FileOutlined aria-hidden="true" />;
    case 'embedding':
      return <DotChartOutlined aria-hidden="true" />;
    default:
      return <FontSizeOutlined aria-hidden="true" />;
  }
}

function modalityLabel(dict: Dictionary, modality: InputModality | OutputModality): string {
  switch (modality) {
    case 'image':
      return dict.models.modeImage;
    case 'video':
      return dict.models.modeVideo;
    case 'embedding':
      return dict.models.modeEmbedding;
    case 'audio':
      return dict.models.modalityAudio;
    case 'file':
      return dict.models.modalityFile;
    default:
      return dict.models.modalityText;
  }
}

function ModalityIcon({ dict, modality }: { dict: Dictionary; modality: InputModality | OutputModality }) {
  const label = modalityLabel(dict, modality);
  return (
    <span
      className={`model-modality-icon model-modality-icon--${modality}`}
      data-tooltip={label}
      aria-label={label}
    >
      {modalityIcon(modality)}
    </span>
  );
}

type SortKey =
  | 'input-asc'
  | 'input-desc'
  | 'output-asc'
  | 'output-desc'
  | 'name-asc'
  | 'name-desc';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 20;
/** When a quick filter is on we need the free/promo rows even if they fall
 * outside the first offset page — catalogue is capped well under this. */
const QUICK_FILTER_PAGE_SIZE = 200;

/**
 * Sort options expressed as the contract's `sort`/`order` pair. Sorting
 * happens in Postgres: sorting only the page the client holds would lie
 * about "cheapest" across the full catalogue.
 */
const SORT_PARAMS: Record<
  SortKey,
  { sort: 'model_id' | 'display_name' | 'price' | 'price_output'; order: 'asc' | 'desc' }
> = {
  'input-asc': { sort: 'price', order: 'asc' },
  'input-desc': { sort: 'price', order: 'desc' },
  'output-asc': { sort: 'price_output', order: 'asc' },
  'output-desc': { sort: 'price_output', order: 'desc' },
  // Sort by customer-facing display_name, not model_id — ids are routing keys
  // (e.g. cohere.embed-v4) and sort in a different order than the labels shown
  // on the cards ("Embed 4" vs "DeepSeek …").
  'name-asc': { sort: 'display_name', order: 'asc' },
  'name-desc': { sort: 'display_name', order: 'desc' },
};

/** Active free-model promo window (`free_until`) — the only promo signal the
 * catalogue exposes today. Both quick chips key off this; a future non-free
 * discount field can split the sets without changing the chip UX. */
function hasActivePromo(m: Model): boolean {
  return m.free_until != null && m.free_until !== '';
}

function promoDaysLeft(freeUntil: string): number {
  const ms = Date.parse(freeUntil) - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.max(1, Math.ceil(ms / 86_400_000));
}

/** Card price: amount on its own line, unit below — narrow two-column cards
 * cannot fit "฿0.96 / 1M โทเคน" inline, and letting the browser wrap mid-unit
 * (or between amount and unit ad hoc) looks broken. */
function ModelPriceLine({ amount, unit }: { amount: string; unit: string }) {
  return (
    <span className="model-price-line">
      <span className="model-price-amount">฿{formatBalance(amount)}</span>
      <span className="model-price-unit">{unit}</span>
    </span>
  );
}

export function Models() {
  const { dict } = useLanguage();
  const [models, setModels] = useState<Model[] | null>(null);
  const [pageInfo, setPageInfo] = useState<OffsetPageInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState(false);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('input-asc');
  // LLMR-136: '' means unfiltered — a customer browsing the whole catalogue
  // was previously the only option, with no way to tell a chat model from an
  // embedding one apart from the name.
  const [mode, setMode] = useState<Mode | ''>('');
  const [filterFree, setFilterFree] = useState(false);
  const [filterPromo, setFilterPromo] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [detail, setDetail] = useState<Model | null>(null);

  const quickFilterOn = filterFree || filterPromo;
  const filtersDirty =
    search.trim() !== '' || mode !== '' || sort !== 'input-asc' || filterFree || filterPromo;

  const resetFilters = () => {
    setSearch('');
    setMode('');
    setSort('input-asc');
    setFilterFree(false);
    setFilterPromo(false);
  };

  const load = useCallback(
    async (wanted: number, size = pageSize) => {
      setBusy(true);
      try {
        const res = await api.models({
          page: wanted,
          page_size: quickFilterOn ? QUICK_FILTER_PAGE_SIZE : size,
          ...SORT_PARAMS[sort],
          q: search.trim() || undefined,
          mode: mode || undefined,
        });
        setModels(res.data);
        setPageInfo(res.page);
        setError(null);
      } catch (e) {
        // The contract does not say whether /models is org-scoped, so a 403
        // here is treated the same as elsewhere — an onboarding account, not
        // a broken page.
        if (e instanceof ApiError && e.status === 403) setOnboarding(true);
        else setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.models.loadError);
      } finally {
        setBusy(false);
      }
    },
    [sort, search, mode, quickFilterOn, pageSize, dict],
  );

  // Any filter or sort change invalidates the current page — the result set it
  // was a position within no longer exists, so this always returns to page 1.
  // Debounced so typing in the search box is one request rather than one per
  // keystroke. The current page number lives in `pageInfo`, echoed by the
  // server, rather than in a second piece of state that could disagree with it.
  useEffect(() => {
    const t = setTimeout(() => void load(1), 250);
    return () => clearTimeout(t);
  }, [load]);

  // Both chips currently match the same `free_until` set (OR of identical
  // predicates). Selecting either or both therefore yields the promo window
  // models — the chip UX stays ready if free vs promo ever diverge in data.
  const visibleModels =
    models === null
      ? null
      : !quickFilterOn
        ? models
        : models.filter((m) => {
            const promo = hasActivePromo(m);
            if (filterFree && promo) return true;
            if (filterPromo && promo) return true;
            return false;
          });

  const resultCount = quickFilterOn
    ? (visibleModels?.length ?? 0)
    : (pageInfo?.total ?? visibleModels?.length ?? 0);

  return (
    <div className="page models-page">
      <PageHeading subtitle={dict.models.subtitle}>{dict.common.models}</PageHeading>

      {error && <div className="err">{error}</div>}

      {onboarding ? (
        <OnboardingNotice area={dict.onboarding.modelsArea} />
      ) : (
        <>
          <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel models-panel models-panel--filters">
            <div className="models-filters">
              <div className="inline-form-field filter-search">
                <label htmlFor="model-search" className="visually-hidden">
                  {dict.models.searchLabel}
                </label>
                <input
                  id="model-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={dict.models.searchPlaceholder}
                />
              </div>
              <div className="models-filter-row">
                <div className="models-quick-filters" role="group" aria-label={dict.common.models}>
                  <button
                    type="button"
                    className={`chip models-filter-chip models-filter-chip--free${filterFree ? ' is-active' : ''}`}
                    aria-pressed={filterFree}
                    onClick={() => setFilterFree((v) => !v)}
                  >
                    <GiftOutlined aria-hidden="true" />
                    {dict.models.filterFree}
                  </button>
                  <button
                    type="button"
                    className={`chip models-filter-chip models-filter-chip--promo${filterPromo ? ' is-active' : ''}`}
                    aria-pressed={filterPromo}
                    onClick={() => setFilterPromo((v) => !v)}
                  >
                    <FireOutlined aria-hidden="true" />
                    {dict.models.filterPromo}
                  </button>
                </div>
                <div className="models-filter-selects">
                  <div className="inline-form-field">
                    <label htmlFor="model-mode" className="visually-hidden">
                      {dict.models.modeLabel}
                    </label>
                    <SelectMenu
                      id="model-mode"
                      aria-label={dict.models.modeLabel}
                      value={mode}
                      onChange={setMode}
                      options={[
                        { value: '', label: dict.models.allModes },
                        { value: 'chat', label: dict.models.modeChat },
                        { value: 'embedding', label: dict.models.modeEmbedding },
                        { value: 'image', label: dict.models.modeImage },
                        { value: 'video', label: dict.models.modeVideo },
                      ]}
                    />
                  </div>
                  <div className="inline-form-field">
                    <label htmlFor="model-sort" className="visually-hidden">
                      {dict.models.sortLabel}
                    </label>
                    <SelectMenu
                      id="model-sort"
                      aria-label={dict.models.sortLabel}
                      value={sort}
                      onChange={setSort}
                      options={[
                        { value: 'input-asc', label: dict.models.sortInputAsc },
                        { value: 'input-desc', label: dict.models.sortInputDesc },
                        { value: 'output-asc', label: dict.models.sortOutputAsc },
                        { value: 'output-desc', label: dict.models.sortOutputDesc },
                        { value: 'name-asc', label: dict.models.sortNameAsc },
                        { value: 'name-desc', label: dict.models.sortNameDesc },
                      ]}
                    />
                  </div>
                </div>
                {filtersDirty && (
                  <button
                    type="button"
                    className="chip models-filter-chip models-filter-chip--reset"
                    onClick={resetFilters}
                  >
                    {dict.models.filterReset}
                  </button>
                )}
              </div>
            </div>
          </motion.section>

          {models === null && !error && <PageLoader />}

          {visibleModels !== null && visibleModels.length === 0 && (
            <EmptyState title={dict.models.emptyTitle}>
              <p className="note">{dict.models.emptyBody}</p>
            </EmptyState>
          )}

          {visibleModels !== null && visibleModels.length > 0 && (
            <>
              <p className="models-result-count" aria-live="polite">
                {quickFilterOn ? dict.models.matchingFilters(resultCount) : dict.models.showingAll(resultCount)}
              </p>
              <motion.div className="model-grid" variants={staggerContainer} initial="hidden" animate="show">
                {visibleModels.map((m) => {
                  const promo = hasActivePromo(m);
                  const days = promo && m.free_until ? promoDaysLeft(m.free_until) : 0;
                  // Border follows the active chip emphasis: promo-only → orange,
                  // otherwise green when the model is in a free window.
                  const emphasizePromo = filterPromo && !filterFree;
                  const cardClass = [
                    'model-card',
                    promo && (emphasizePromo ? 'model-card--promo' : 'model-card--free'),
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <motion.button
                      key={m.model_id}
                      type="button"
                      variants={fadeUp}
                      className={cardClass}
                      onClick={() => setDetail(m)}
                    >
                      <div className="model-card-head">
                        {/* Vendor mark (who made the model — Anthropic, Z.AI, ...),
                            not a provider badge (who hosts it — that stays hidden
                            per the multi-provider routing migration: a customer
                            picks a model, never a provider, and the gateway routes
                            to whichever one is best behind the scenes). glm-5.2
                            gets the same Z.AI mark whether Fireworks or DashScope
                            actually served the last request. */}
                        <ModelVendorMark modelId={m.model_id} size={40} />
                        <div className="model-card-title" data-tooltip={m.display_name}>
                          <h3>{m.display_name}</h3>
                        </div>
                        {/* LLMR-136: shown for every model, not just the exceptions — a
                            one-glance type marker (chat vs embedding vs the not-yet-built
                            image/video) instead of a badge a customer only notices when
                            it's unusual. data-tooltip (not title) for the same fast custom
                            hover label Team.tsx/Keys.tsx row actions already use — a native
                            title tooltip waits ~1s and would show twice alongside it. */}
                        <span
                          className={`model-mode-icon model-mode-icon--${m.mode}`}
                          data-tooltip={modeLabel(dict, m.mode)}
                          aria-label={modeLabel(dict, m.mode)}
                        >
                          {modeIcon(m.mode)}
                        </span>
                      </div>
                      {promo && (
                        <div className="model-card-badges">
                          <span className="badge badge--model-free">
                            <GiftOutlined aria-hidden="true" /> {dict.models.freeBadge}
                          </span>
                          <span className="badge badge--model-promo">
                            <FireOutlined aria-hidden="true" /> {dict.models.promoBadge(days)}
                          </span>
                        </div>
                      )}
                      <dl className="model-card-prices">
                        <div>
                          <dt>{dict.models.input}</dt>
                          <dd>
                            <ModelPriceLine amount={m.price_input.amount} unit={dict.models.perMillionTok} />
                          </dd>
                        </div>
                        <div>
                          <dt>{dict.models.output}</dt>
                          <dd>
                            <ModelPriceLine amount={m.price_output.amount} unit={dict.models.perMillionTok} />
                          </dd>
                        </div>
                      </dl>
                      {(m.measured_ttft_ms != null ||
                        m.measured_tps != null ||
                        !m.supports_streaming) && (
                        <div className="model-card-foot">
                          {m.measured_ttft_ms != null && <span>{m.measured_ttft_ms} ms TTFT</span>}
                          {m.measured_tps != null && <span>{m.measured_tps} tok/s</span>}
                          {!m.supports_streaming && <span className="muted">{dict.models.noStreaming}</span>}
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>

              {!quickFilterOn && (
                <OffsetPager
                  page={pageInfo}
                  busy={busy}
                  onPage={(p) => void load(p)}
                  pageSize={pageSize}
                  pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
                  onPageSizeChange={setPageSize}
                />
              )}
            </>
          )}
        </>
      )}

      <AnimatePresence>
        {detail && <ModelDetail model={detail} onClose={() => setDetail(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ModelDetail({ model, onClose }: { model: Model; onClose: () => void }) {
  const { dict } = useLanguage();
  const [view, setView] = useState<'detail' | 'quickstart'>('detail');
  const promo = hasActivePromo(model);
  const days = promo && model.free_until ? promoDaysLeft(model.free_until) : 0;

  return (
    <Modal
      title={view === 'detail' ? model.display_name : dict.models.quickStart.title}
      onClose={onClose}
      wide={view === 'quickstart'}
    >
      {view === 'detail' ? (
        <>
          {promo && model.free_until && (
            <div className="model-card-badges" style={{ marginBottom: 12 }}>
              <span className="badge badge--model-free">
                <GiftOutlined aria-hidden="true" /> {dict.models.freeBadge}
              </span>
              <span className="badge badge--model-promo">
                <FireOutlined aria-hidden="true" /> {dict.models.promoBadge(days)}
              </span>
            </div>
          )}
          {promo && model.free_until && (
            <p className="note" style={{ marginTop: 0 }}>
              {dict.models.freeUntil(formatDateTime(model.free_until))}
            </p>
          )}
          <dl className="detail-grid">
            <div>
              <dt>{dict.models.modeLabel}</dt>
              <dd>{modeLabel(dict, model.mode)}</dd>
            </div>
            <div>
              <dt>{dict.models.inputPrice}</dt>
              <dd>
                <ModelPriceLine amount={model.price_input.amount} unit={`/ 1M ${dict.models.tokensSuffix}`} />
              </dd>
            </div>
            <div>
              <dt>{dict.models.outputPrice}</dt>
              <dd>
                <ModelPriceLine amount={model.price_output.amount} unit={`/ 1M ${dict.models.tokensSuffix}`} />
              </dd>
            </div>
            {model.price_cache_read && (
              <div>
                <dt>{dict.models.cacheRead}</dt>
                <dd>
                  <ModelPriceLine
                    amount={model.price_cache_read.amount}
                    unit={`/ 1M ${dict.models.tokensSuffix}`}
                  />
                </dd>
              </div>
            )}
            {model.price_cache_write && (
              <div>
                <dt>{dict.models.cacheWrite}</dt>
                <dd>
                  <ModelPriceLine
                    amount={model.price_cache_write.amount}
                    unit={`/ 1M ${dict.models.tokensSuffix}`}
                  />
                </dd>
              </div>
            )}
            {model.context_window != null && (
              <div>
                <dt>{dict.models.contextWindow}</dt>
                <dd>
                  {model.context_window.toLocaleString()} {dict.models.tokensSuffix}
                </dd>
              </div>
            )}
            <div>
              <dt>{dict.models.streaming}</dt>
              <dd>{model.supports_streaming ? dict.models.supported : dict.models.notSupported}</dd>
            </div>
            <div>
              <dt>{dict.models.modalitiesLabel}</dt>
              <dd>
                <div className="model-modalities-row">
                  {model.input_modalities.map((m) => (
                    <ModalityIcon key={m} dict={dict} modality={m} />
                  ))}
                  <span className="model-modalities-arrow" aria-hidden="true">
                    →
                  </span>
                  <ModalityIcon dict={dict} modality={model.output_modality} />
                </div>
              </dd>
            </div>
            {model.measured_ttft_ms != null && (
              <div>
                <dt>{dict.models.medianTtft}</dt>
                <dd>{dict.models.msFromBangkok(model.measured_ttft_ms)}</dd>
              </div>
            )}
            {model.measured_tps != null && (
              <div>
                <dt>{dict.models.medianThroughput}</dt>
                <dd>{dict.models.tokSecFromBangkok(model.measured_tps)}</dd>
              </div>
            )}
          </dl>

          <div className="modal-actions">
            <button type="button" className="ghost" onClick={onClose}>
              {dict.common.close}
            </button>
            <button type="button" onClick={() => setView('quickstart')}>
              {dict.models.quickStart.tryThisModel}
            </button>
          </div>
        </>
      ) : (
        <ModelQuickStartView
          modelId={model.model_id}
          onBack={() => setView('detail')}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}
